import { ISeriesApi } from 'lightweight-charts'
import MenuItem from '@mui/material/MenuItem'
import {Collapse, Divider, List, ListItemIcon, ListItemText} from '@mui/material'
import { AppMenu } from '../../common/AppMenu'
import React, {memo, useCallback, useContext, useEffect, useMemo, useRef, useState} from 'react'
import AddToWatchlistMenuItem from '../seriesMetaDisplay/seriesGroupMenu/menuItems/addToWatchlistMenuItem'
import {SeriesGroup} from '../chart.typings'
import AlertContextMenuItem from './AlertContextMenuItem'
import { CHART_CONTEXT_MENU_ATTR, CHART_CONTEXT_MENU_SELECTOR, ChartContextMenuAnchor } from './ChartContextMenuHost'
import {AppContext} from '../../../App'
import {ChartViewKey, INITIAL_CANDLE_COUNT, MULTIPANE_FULLSCREEN_MODE_ID} from '../../../assets/constants'
import {useDispatch, useSelector} from 'react-redux'
import {selectActiveLayoutId, selectPrimarySecurity} from '../../../selectors/home'
import {OptionType, Security, SecurityCategory_CategoryCode} from '../../../proto/generated/security'
import { isTradeableSecurity } from '../../../util/cashInstrument'
import { Broker_OrderType, Broker_Variety, OrderType, Product, TransactionType, Validity } from '../../../proto/generated/order'
import {setQuickOrderData} from '../../../reducer/orders.reducer'
import {selectReferenceSecurityMap} from '../../../selectors/security'
import store from '../../../store'
import HorizontalLine from '../../../assets/icons/drawingTools/horizontalLine'
import {selectActiveChartId} from '../../../selectors/chart'
import {v4 as uuid} from 'uuid'
import StrategiesMenu from '../../predefinedStrategies'
import {addStrategyOrder, calculateDifferenceInDays, orderStrategyType} from '../../../util/strategyBuilder'
import {getExpressionSecuritiesCoefficients} from '../../../util/expression'
import {selectUnderlyingOptionsTradeData} from '../../../selectors/options'
import {getSecuritiesFromSecuritiesObj, getSecurityAsync, getSecurityKey, isFnOSecurity} from '../../../util/security'
import {FullScreenToggle, selectCommonStore, selectMultiPaneFullScreen, selectWidgetPaneId} from '../../../selectors/common'
import {CustomOptionSecurityType, optionsTradesDataType, optionTradeDataType} from '../../../pages/typings/options.typings'
import {fetchOptionChainV2, fetchOptionChainV2Initial} from '../../../util/options'
import FullScreenIcon from '../../../assets/icons/FullScreenIcon'
import {setWidgetsPaneId, toggleFullscreen} from '../../../actions/common.actions'
import {selectLayout, selectLayoutWidgets} from '../../../selectors/widgetDashboard'
import FullScreenReverseIcon from '../../../assets/icons/FullScreenReverseIcon'
import {exchangeToJSON, TimeBucket_TimeSeries} from '../../../proto/generated/common'
import {selectUnderlyingFuturesTradeData} from '../../../selectors/futures'
import {generatePath, useNavigate} from 'react-router-dom'
import ROUTES from '../../../assets/routes'
import {fetchSecuritySnapshot} from '../../../services/apis/candles'
import {GetOptionChainRequestV2} from '../../../proto/generated/candle-data-depot-option-chain'
import {StrategyGroup} from '../../strategyTemplate/StrategyTemplate.types'
import {StrategyTemplateProto} from '../../../proto/generated/strategy'
import {parseStrategyTemplateOrders} from '../../strategyTemplate/StrategyParser'
import {setStrategyGroup, TradeOrder, updateTradeOrders} from '../../../reducer/strategyBuilder.reducer'
import {WidgetListItems} from '../../../util/sidebar'
import commonActions from '../../../actions/common.actions'
import AnalyticsManager from '../../../analytics/AnalyticsManager'
import ThinArrowDownIcon from "../../../assets/icons/ThinArrowDownIcon";
import ThinArrowRightIcon from "../../../assets/icons/ThinArrowRightIcon";
import ThinArrowUpIcon from "../../../assets/icons/ThinArrowUpIcon";
import Trade from "../../../assets/icons/Trade";
import ResetIcon from "../../../assets/icons/ResetIcon";
import CopyIcon from "../../../assets/icons/CopyIcon";
import { DrawingDisplayType } from '../drawingTools/drawing.interface'
import type OrdersOnChartTool from '../drawingTools/ordersOnChartTool/OrdersOnChartTool'
import { isDatePassed, roundToNearestMultiple } from '../../../util/tradepanel'
import { FeatureCategory, FeatureName } from '../../adminUserRestrictions/Restrictions.constants'
import { useUser } from '../../../context/UserContext'
import { selectOrderType } from '../../../selectors/strategyBuilder'
import { selectIndicators } from '../../../selectors/chart'
import { SeriesGroupCategory } from '../../../pages/home'

interface Props {
    anchorPosition: ChartContextMenuAnchor
    setAnchorPosition: React.Dispatch<React.SetStateAction<ChartContextMenuAnchor>>
    viewKey: string
}

const CHART_MENU_KEYS = [
    'reset-chart',
    'alert',
    'copy-price',
    // "quick-trade-buy",
    // "quick-trade-sell",
    'limit-order',
    'stop-loss-order',
    'buy-spread',
    'sell-spread',
    'fut-limit-order',
    'fut-stop-loss-order',
    'trade-options',
    'view-full-screen',
    'add-to-watchlist'
]

const BUTTON_MENU_KEYS = [
    'alert',
    'limit-order',
    'stop-loss-order',
    'buy-spread',
    'sell-spread',
    'fut-limit-order',
    'fut-stop-loss-order',
    'trade-options',
    'horizontal-line'
]


const tradeOnChartExclusions = [
    'limit-order',
    'stop-loss-order',
    'buy-spread',
    'sell-spread',
]

const PAPER_TRADING_EXCLUSION_KEYS = [
    'stop-loss-order',
    'fut-stop-loss-order'
]

const ChartContextMenuOptions = (props: Props) => {
    const {anchorPosition, setAnchorPosition, viewKey} = props
    const dispatch = useDispatch()
    const ref = useRef<HTMLLIElement>(null)
    const appContext = useContext(AppContext)
    const chart = appContext.getChart(viewKey)
    const orderType = useSelector(selectOrderType)
    const indicators = useSelector(selectIndicators)
    const activeChartId = useSelector(selectActiveChartId)
    const {primary, key, expressionMode, expressionSecurities, underlying} = useSelector(selectPrimarySecurity)
    const [security, setSecurity] = useState<Security | undefined>(undefined)
    useEffect(() => { if (key) getSecurityAsync(key).then(setSecurity) }, [key])
    const refSecurityMap = useSelector(selectReferenceSecurityMap)
    const mainSecurity = expressionMode ? underlying : (primary && isFnOSecurity(primary) ? underlying : primary)
    const menuOpen = anchorPosition.x !== 0 && anchorPosition.y !== 0
    const [positionRelativeToBidAsk, setPositionRelativeToBidAsk] = useState<string>()
    const [openTradeOptions, setOpenTradeOptions] = useState(false)
    const [clickedLtp, setClickedLtp] = useState(0)
    const underlyingToken = (mainSecurity?.exchange != null && mainSecurity?.code != null)
        ? getSecurityKey(mainSecurity.exchange, mainSecurity.code)
        : ''
    const underlyingOptionsExpiries = useSelector(selectUnderlyingOptionsTradeData(underlyingToken))
    const underlyingFuturesTradeData = useSelector(selectUnderlyingFuturesTradeData(underlyingToken))
    const { user: { hasAccess } } = useUser()
    const [underlyingCurrentFutureSecurities, setUnderlyingCurrentFutureSecurities] = useState<(Security | undefined)[]>([])
    useEffect(() => {
        if (!underlyingFuturesTradeData?.length) { setUnderlyingCurrentFutureSecurities([]); return }
        Promise.all((underlyingFuturesTradeData ?? []).map((c) => {
            const sKey = getSecurityKey(c.security?.exchange ?? 0, c.security?.code ?? '')
            return getSecurityAsync(sKey)
        })).then(setUnderlyingCurrentFutureSecurities)
    }, [underlyingFuturesTradeData])
    const {rightSideWidget} = useSelector(selectCommonStore)
    const selectedPaneId = useSelector(selectWidgetPaneId)
    const activeLayoutId = useSelector(selectActiveLayoutId)
    const activeLayout = useSelector(selectLayout(activeLayoutId))
    const widgets = useSelector(selectLayoutWidgets(activeLayoutId))
    const activeWidget = useMemo(() => widgets[Number(viewKey.split('-')[1]) ?? 0], [viewKey, widgets])
    const navigate = useNavigate()
    const isFullScreen = useSelector(FullScreenToggle)
    const handleClose = useCallback(() => {
        setAnchorPosition((prev) => ({ ...prev, x: 0, y: 0 }))
    }, [setAnchorPosition])
    const allowTradeOnChart = hasAccess(FeatureCategory.CHART, '', FeatureName.TRADE_FROM_CHART)
    const isPrimaryPaneContext = useMemo(() => {
        const sg = anchorPosition.seriesGroup as (SeriesGroup & { primary?: boolean }) | undefined
        if (!sg) return true
        return !!sg.primary
    }, [anchorPosition.seriesGroup])
    const contextAlertLabel = useMemo(() => {
        if (isPrimaryPaneContext) return primary?.symbol
        const paneSeriesGroup = anchorPosition.seriesGroup
        if (paneSeriesGroup?.category === SeriesGroupCategory.SERIES_INDICATOR) {
            const indicator = (indicators || []).find((i) => i.code === paneSeriesGroup.scriptCode)
            return indicator?.shortName || indicator?.name || paneSeriesGroup.name || primary?.symbol
        }
        return anchorPosition.seriesGroup?.name || primary?.symbol
    }, [isPrimaryPaneContext, anchorPosition.seriesGroup, primary?.symbol, indicators])
    const isIndicatorPaneAlertSupported = useMemo(() => {
        const sg = anchorPosition.seriesGroup
        if (!sg || sg.category !== SeriesGroupCategory.SERIES_INDICATOR) return true
        const indicator = (indicators || []).find((i) => i.code === sg.scriptCode)
        return Boolean(indicator?.enabled && indicator?.isAlertSupported)
    }, [anchorPosition.seriesGroup, indicators])
    const paneIndicatorAlertSeriesGroups = useMemo(() => {
        if (!isPrimaryPaneContext || !chart) return []
        const paneId = anchorPosition.seriesGroup?.id
            ? chart.seriesGroupRef[anchorPosition.seriesGroup.id]?.pane?.id
            : chart.primarySeriesGroup?.pane?.id
        if (!paneId) return []
        const supportedCodes = new Set(
            (indicators || []).filter((i) => i.enabled && i.isAlertSupported).map((i) => i.code)
        )
        return Object.values(chart.seriesGroupRef)
            .filter((sgRef) =>
                sgRef.pane?.id === paneId &&
                sgRef.config?.category === SeriesGroupCategory.SERIES_INDICATOR &&
                supportedCodes.has(sgRef.config.scriptCode)
            )
            .map((sgRef) => sgRef.config)
    }, [isPrimaryPaneContext, chart, anchorPosition.seriesGroup?.id, indicators])

    const securityValid = useMemo(() => {
        if(security?.expiry?.expiry) {
            return isDatePassed(security.expiry.expiry) ? false : true
        }
        return true
    }, [security])

    const copyPriceToClipBoard = useCallback((price: string) => {
        AnalyticsManager.logEvent('Copy Price Clicked', {source: 'chart-grid'})
        try {
            navigator?.clipboard?.writeText(price)
        } catch (e) {
            console.error(e)
        }
        handleClose()
    }, [handleClose])

    const resetTimeScale = useCallback(() => {
        AnalyticsManager.logEvent('Reset Chart View Clicked', {source: 'chart-grid', symbol: primary?.symbol})
        appContext.getChart(viewKey)?.resetTimeScale()
        handleClose()
    }, [appContext, handleClose, viewKey])

    const showSpreadTradeOption: boolean = useMemo(() => {
        if (expressionMode) {
            if (key.includes('/') || key.includes('^')) return false
            const coefficientObject = getExpressionSecuritiesCoefficients(key)
            let allNums = true
            Object.keys(coefficientObject).forEach(c => {
                if (!Number.isInteger(coefficientObject[c])) allNums = false
            })
            if (!allNums) return false
        }
        return expressionSecurities.length > 0 ?
            expressionSecurities.every(sec => [SecurityCategory_CategoryCode.FUTURES, SecurityCategory_CategoryCode.OPTIONS].includes(sec.categoryCode || SecurityCategory_CategoryCode.CATEGORY_CODE_UNKNOWN)) &&
            expressionSecurities.every(sec => (sec.underlyingSecurityCode === expressionSecurities[0].underlyingSecurityCode) && (sec.exchange === expressionSecurities[0].exchange))
            : false
    }, [expressionMode, expressionSecurities, key])

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const buySpread = useCallback(async (sell = false) => {
        AnalyticsManager.logEvent(`${sell ? 'Sell' : 'Buy'} Spread Clicked`, {source: 'chart-grid'})
        handleClose()
        const security = expressionSecurities[0]

        const securities = await getSecuritiesFromSecuritiesObj([{
            exchange: security.exchange,
            code: security.underlyingSecurityCode ?? ''
        }])
        if (securities) {
            // setPrimarySecurity(getSecurityKey(security.exchange, security.underlyingSecurityCode ?? ''), securities, false)
            const coefficientObject = getExpressionSecuritiesCoefficients(key)
            const orders: TradeOrder[] = []
            const keys = Object.keys(coefficientObject)
            let currentOptionExpiries = underlyingOptionsExpiries
            if (!(currentOptionExpiries && currentOptionExpiries.length)) {
                const {optionsData} = await fetchOptionChainV2Initial(securities[0], dispatch)
                currentOptionExpiries = optionsData
            }

            for (let i = 0; i < keys.length; i++) {
                const coeff = keys[i]
                let sec = await getSecurityAsync(coeff)
                if (sec?.derived) {
                    if (!refSecurityMap[getSecurityKey(sec.exchange, sec.code)]) {
                        const snp = await fetchSecuritySnapshot({security: sec, dispatch})
                        const refSecurity = snp.referencedSecurity
                        sec = await getSecurityAsync(getSecurityKey(sec.exchange, refSecurity?.code ?? ''))
                    } else {
                        sec = await getSecurityAsync(refSecurityMap[getSecurityKey(sec.exchange, sec.code)])
                    }
                }
                if (currentOptionExpiries) {
                    const isFuture = sec?.categoryCode === SecurityCategory_CategoryCode.FUTURES
                    if (isFuture && underlyingCurrentFutureSecurities) {
                        if (sec) {
                            orders.push({
                                clientOrderId: uuid(),
                                quantity: Math.abs(coefficientObject[coeff]) * (sec.lotSize ?? 1),
                                security: sec,
                                selected: true,
                                transactionType: coefficientObject[coeff] > 0 ? TransactionType.BUY : TransactionType.SELL
                            })
                        }
                        continue
                    }
                    const expiryIndex = currentOptionExpiries.findIndex(ex => Number(ex.expirySeconds) === sec?.expiry?.expiry)
                    if (!currentOptionExpiries[expiryIndex]?.trades.length) {
                        const payload: GetOptionChainRequestV2 = {
                            security: {code: sec!.code, exchange: sec!.exchange},
                            expiries: [sec?.expiry?.expiry ?? 0],
                            noOfStrikes: -1,
                            requestedFields: [],
                            timeBucket: {
                                timeSeries: TimeBucket_TimeSeries.MINUTE,
                                value: 1
                            },
                            sendFutures: false
                        }
                        const {optionsData}: { optionsData: optionsTradesDataType[] } = await fetchOptionChainV2(payload, dispatch, currentOptionExpiries, 'both', false)
                        currentOptionExpiries = optionsData
                    }
                    const data = currentOptionExpiries[expiryIndex]?.trades.find(trade => trade.strikeCol.strike === sec?.strikePrice)
                    if (data) {
                        const option = sec?.optionType === OptionType.CE ? data.callOption : data.putOption
                        if(option) {
                            let transactionType = coefficientObject[coeff] > 0 ? TransactionType.BUY : TransactionType.SELL
                            if(sell) {
                                transactionType = transactionType === TransactionType.BUY ? TransactionType.SELL : TransactionType.BUY
                            }
                            orders.push({
                                clientOrderId: uuid(),
                                quantity: Math.abs(coefficientObject[coeff]) * (sec?.lotSize ?? 1),
                                security: sec!,
                                price: (transactionType === TransactionType.BUY ? option.topAsk : option.topBid) || 0,
                                selected: true,
                                transactionType
                            })
                        }
                    }
                }
            }
            commonActions.dispatchRightSideWidget(dispatch, WidgetListItems.TRADE)
            dispatch(updateTradeOrders(orders));
        }
    }, [handleClose, expressionSecurities, dispatch, key, underlyingOptionsExpiries, underlyingFuturesTradeData, refSecurityMap, underlyingCurrentFutureSecurities, rightSideWidget, underlyingToken, navigate])

    const showResetChartButton = useMemo(() => {
        try {
            if (!chart?.chartApi) return true
            const currentVisibleLogicalRange = chart.chartApi.timeScale().getVisibleLogicalRange()
            const candles = chart.getRenderedPrimaryData()
            const currentFrom = Math.max(candles.length - INITIAL_CANDLE_COUNT, 0)
            const currentTo = candles.length + 3
            const expectedFrom = currentVisibleLogicalRange?.from as number
            const expectedTo = currentVisibleLogicalRange?.to as number
            if ((currentFrom === expectedFrom && currentTo === expectedTo) && chart.chartApi.priceScale('right').options().autoScale) {
                return false
            }
            return true
        } catch (e) {
            return true
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [chart, anchorPosition])

    const showQuickTradeButton = useMemo(() => {
        if (isTradeableSecurity(primary) || (primary?.categoryCode === SecurityCategory_CategoryCode.FUTURES && !primary.derived)) {
            return true
        }
        return false
    }, [primary])

    const showFullScreenChart = useCallback(() => {
        AnalyticsManager.logEvent(`${!isFullScreen ? 'Maximize' : 'Restore'} Chart Clicked`, { source: 'chart-grid' })
        const newPaneId = activeWidget?.slotId === selectedPaneId ? null : activeWidget?.slotId ?? 0
        dispatch(setWidgetsPaneId(newPaneId))
        dispatch(toggleFullscreen())
    }, [activeWidget, selectedPaneId, dispatch])

    const handleQuickTrade = useCallback((transaction: TransactionType, orderType: OrderType = OrderType.NORMAL, brokerOrderType = Broker_OrderType.MARKET_ORDER, product: Product = Product.CNC, price: number | undefined = undefined, isFuture: boolean = false) => {
        const tradeSecurity = isFuture && underlyingCurrentFutureSecurities.length > 0
            ? underlyingCurrentFutureSecurities[0] 
            : security
        if (!tradeSecurity) return
        AnalyticsManager.logEvent(`${transaction === TransactionType.BUY ? 'Buy' : 'Sell'} Clicked`, {
            source: 'chart-grid',
            symbol: tradeSecurity.symbol
        })
        let quantity = tradeSecurity.lotSize || 1
        let finalProduct = product
        const intraday = finalProduct === Product.INTRADAY

        if (!intraday) {
            if (tradeSecurity.categoryCode === SecurityCategory_CategoryCode.FUTURES || tradeSecurity.categoryCode === SecurityCategory_CategoryCode.OPTIONS) {
                finalProduct = Product.MARGIN
            } else {
                finalProduct = Product.CNC
            }
        }
        dispatch(
            setQuickOrderData({
                openDialog: true,
                mode: orderType === OrderType.TRIGGER ? 'trigger' : 'regular',
                type: 'trade',
                isSquareOffOrder: false,
                order: {
                    transactionType: transaction,
                    security: tradeSecurity,
                    editOrder: undefined,
                    brokerOrderType: brokerOrderType,
                    orderType,
                    product: finalProduct,
                    price: brokerOrderType === Broker_OrderType.MARKET_ORDER ? undefined : price,
                    triggerPrice: orderType === OrderType.TRIGGER ? price : undefined,
                    quantity,
                    maxQuantity: 0
                }
            })
        )
        handleClose()
    }, [underlyingCurrentFutureSecurities, dispatch, handleClose, security])

    const handleQuickChartOrder = useCallback((transaction: TransactionType, orderType: OrderType = OrderType.NORMAL, brokerOrderType = Broker_OrderType.LIMIT_ORDER, price: number | undefined = undefined) => {
        const chartDrawing = chart?.drawingManager?.getDrawing(chart?.ordersOnChartDrawingId) as OrdersOnChartTool
        if(securityValid) {
            if(chartDrawing && mainSecurity) {
                const newPrice = Number(chartDrawing.roundPriceToTickSize(Number(price)/(mainSecurity.priceMultiplier??100))) * (mainSecurity?.priceMultiplier??100)
                chartDrawing.setPendingOpenOrder({
                    security,
                    transactionType: transaction,
                    orderType,
                    brokerOrderType,
                    quantity: 1 * (security?.lotSize??1),
                    isBasket: false,
                    orderDate: Date.now(),
                    product: Product.INTRADAY,
                    price: orderType===OrderType.NORMAL ? newPrice : 0,
                    triggerPrice: orderType===OrderType.TRIGGER ? newPrice : 0,
                    brokerVariety: Broker_Variety.REGULAR,
                    validity: Validity.DAY,
                    clientOrderId: uuid(),
                    immediateExecutePaperOrder: false
                })
            } else {
                handleQuickTrade(transaction, orderType, brokerOrderType, Product.INTRADAY, roundToNearestMultiple(Number(price)/(security?.priceMultiplier ?? 100), positionRelativeToBidAsk === 'above' ? TransactionType.BUY : TransactionType.SELL, false, (expressionMode ? expressionSecurities[0] : security)?.tickSize / (security?.priceMultiplier ?? 100)) * (security?.priceMultiplier ?? 100))
            }
        }
        handleClose()
    }, [chart, handleClose, security, expressionMode, handleQuickTrade, positionRelativeToBidAsk, securityValid])

    const handleStrategyTemplateClick = useCallback(async (template: StrategyTemplateProto) => {
        try {
            if (!mainSecurity) {
                console.error("Main security not found")
                return
            }
            
            const tradeOrders = await parseStrategyTemplateOrders(template, mainSecurity, underlyingOptionsExpiries, orderType)
            commonActions.dispatchRightSideWidget(dispatch, WidgetListItems.TRADE)
            dispatch(setStrategyGroup(`${mainSecurity.displayName} - ${template.name}`))
            dispatch(updateTradeOrders(tradeOrders))
            setOpenTradeOptions(false)
            handleClose()
        } catch (error) {
            console.error("Error parsing strategy template:", error)
        }
    }, [mainSecurity, dispatch, handleClose, underlyingOptionsExpiries, orderType])

    const getPositionOptions = useCallback(() => {
        const lowerLimit = (clickedLtp - 0.0025 * clickedLtp) / (security?.priceMultiplier ?? 100)
        const upperLimit = (clickedLtp + 0.0025 * clickedLtp) / (security?.priceMultiplier ?? 100)
        if (Number(anchorPosition.price) < lowerLimit) {
            return <StrategiesMenu giveList
                                   listType={StrategyGroup.BEARISH}
                                   onStrategyClicked={handleStrategyTemplateClick}/>
        } else if (Number(anchorPosition.price) > upperLimit) {
            return <StrategiesMenu giveList
                                   listType={StrategyGroup.BULLISH}
                                   onStrategyClicked={handleStrategyTemplateClick}/>
        }
        return <StrategiesMenu giveList
                               listType={StrategyGroup.NEUTRAL}
                               onStrategyClicked={handleStrategyTemplateClick}/>
    }, [clickedLtp, anchorPosition, security, handleStrategyTemplateClick])

    const handleDrawHorizontalLine = useCallback(() => {
        AnalyticsManager.logEvent('Draw Horizontal Line Clicked', {source: 'chart-grid', price: anchorPosition.price})
        const chartInstance = appContext.getChart(activeChartId)
        if (!chartInstance?.chartApi) return
        let paneId = chartInstance.primarySeriesGroup?.pane.id
        let seriesId = chartInstance.primarySeriesGroup?.series[0].id
        let targetSeries = chartInstance.primarySeriesGroup?.getPrimarySeriesApi()
        if (anchorPosition.seriesGroup?.id) {
            const sgRef = chartInstance.seriesGroupRef[anchorPosition.seriesGroup.id]
            if (sgRef) {
                const pid = sgRef.pane.id
                const dr = chartInstance.getDrawingSeries(pid) as [] | [string, ISeriesApi<any>]
                if (dr.length === 2 && dr[1]) {
                    paneId = pid
                    seriesId = dr[0]
                    targetSeries = dr[1]
                }
            }
        }
        if (!paneId || !seriesId || !targetSeries) return
        const seriesData = targetSeries.data()
        if (!seriesData.length) return
        const time = seriesData[seriesData.length - 1].time
        const drawings = chartInstance.drawingManager.addDrawing(
            'horizontalLine',
            DrawingDisplayType.DRAWING,
            {
                drawingType: 'horizontalLine'
            },
            {
                lineColor: {
                    color: '#2962ff',
                    opacity: 1
                },
                lineType: 'normal',
                lineWidth: 2,
                point1: {
                    id: 'p1',
                    x: time,
                    y: Number(anchorPosition.price),
                    coordinates: {
                        x: chartInstance.chartApi.timeScale().width() / 2,
                        y: targetSeries.priceToCoordinate(Number(anchorPosition.price)) ?? 0
                    }
                }
            },
            uuid().replace(/[-]*/g, '').substring(0, 10),
            {
                p1: {
                    id: 'p1',
                    x: time,
                    y: Number(anchorPosition.price),
                    coordinates: {
                        x: chartInstance.chartApi.timeScale().width() / 2,
                        y: targetSeries.priceToCoordinate(Number(anchorPosition.price))
                    }
                }
            },
            paneId,
            seriesId
        )
        setTimeout(() => {
            drawings?.toggleFocus(true)
        }, 100)
        handleClose()

    }, [appContext, activeChartId, anchorPosition, handleClose])

    useEffect(() => {
        let securityToFetch: Security | undefined = undefined
        if (anchorPosition.x === 0 && anchorPosition.y === 0) return
        if (!expressionMode) {
            if (!security || !security.hasFo) return
            securityToFetch = security.hasFo ? security : {
                exchange: security.exchange,
                code: security.underlyingSecurityCode ?? ''
            }
        } else if (expressionMode && showSpreadTradeOption) {
            const security = expressionSecurities[0]
            securityToFetch = security ? {
                exchange: security.exchange,
                code: security.underlyingSecurityCode ?? ''
            } : undefined
        }
        if (securityToFetch === undefined || !securityToFetch) return

        if (!(underlyingOptionsExpiries && underlyingOptionsExpiries.length)) {
            fetchOptionChainV2Initial(securityToFetch, dispatch)
        }
    }, [anchorPosition])

    // Freeze bid/ask side when the menu opens — do not recompute on live candle ticks.
    useEffect(() => {
        if (!menuOpen) {
            setPositionRelativeToBidAsk(undefined)
            setOpenTradeOptions(false)
            return
        }
        if (!isPrimaryPaneContext) return
        const chartInstance = appContext.getChart(viewKey)
        const lastCandleValues = chartInstance?.dataManager?.data?.[key]?.slice(-1)?.[0]
        const topAsk = (lastCandleValues?.topAsk ?? 0) / (security?.priceMultiplier ?? 100)
        const topBid = (lastCandleValues?.topBid ?? 0) / (security?.priceMultiplier ?? 100)
        const anchorPrice = Number(anchorPosition.price)
        if (security?.categoryCode === SecurityCategory_CategoryCode.INDEX) {
            setPositionRelativeToBidAsk(undefined)
        } else if (anchorPrice >= topAsk) {
            setPositionRelativeToBidAsk('above')
        } else if (anchorPrice <= topBid) {
            setPositionRelativeToBidAsk('below')
        } else {
            setPositionRelativeToBidAsk(undefined)
        }
    }, [menuOpen, anchorPosition.price, anchorPosition.x, anchorPosition.y, isPrimaryPaneContext, appContext, viewKey, key, security?.categoryCode, security?.priceMultiplier])

    const optionsList = useMemo(() => {
        if (!menuOpen) return []
        return [
        {
            key: 'reset-chart',
            element: <>
                <ListItemIcon>
                    <ResetIcon/>
                </ListItemIcon>
                <ListItemText>
                    Reset chart view
                </ListItemText>
            </>,
            clickHandler: resetTimeScale,
            visible: showResetChartButton,
            showDivider: true
        },
        {
            key: 'copy-price',
            element: <>
                <ListItemIcon>
                    <CopyIcon/>
                </ListItemIcon>
                <ListItemText>
                    {`Copy price ${anchorPosition.price}`}
                </ListItemText>
            </>,
            clickHandler: () => copyPriceToClipBoard(anchorPosition.price),
            visible: true,
            showDivider: false
        },
        {
            key: 'alert',
            element: <>
                <AlertContextMenuItem viewKey={viewKey} symbolName={contextAlertLabel}
                                      customPriceValue={anchorPosition.price} handleCloseMenu={handleClose}
                                      seriesGroup={anchorPosition.seriesGroup}/>
            </>,
            clickHandler: () => {
            },
            visible: (isPrimaryPaneContext ? securityValid : true) && isIndicatorPaneAlertSupported,
            showDivider: paneIndicatorAlertSeriesGroups.length === 0
        },
        ...paneIndicatorAlertSeriesGroups.map((seriesGroup, index) => ({
            key: `alert-indicator-${seriesGroup.id}`,
            element: (
                <>
                    <AlertContextMenuItem
                        viewKey={viewKey}
                        symbolName={
                            indicators.find((i) => i.code === seriesGroup.scriptCode)?.shortName ||
                            indicators.find((i) => i.code === seriesGroup.scriptCode)?.name ||
                            seriesGroup.name ||
                            contextAlertLabel
                        }
                        customPriceValue={anchorPosition.price}
                        handleCloseMenu={handleClose}
                        seriesGroup={seriesGroup}
                    />
                </>
            ),
            clickHandler: () => {},
            visible: isPrimaryPaneContext ? securityValid : true,
            showDivider: index === paneIndicatorAlertSeriesGroups.length - 1
        })),
        {
            key: 'quick-trade-buy',
            element: <>
                <ListItemIcon>
                </ListItemIcon>
                <ListItemText>
                    Buy {primary?.symbol}
                </ListItemText>
            </>,
            clickHandler: () => handleQuickTrade(TransactionType.BUY),
            visible: showQuickTradeButton && securityValid,
            showDivider: false
        },
        {
            key: 'quick-trade-sell',
            element: <>
                <ListItemIcon>
                </ListItemIcon>
                <ListItemText>
                    Sell {primary?.symbol}
                </ListItemText>
            </>,
            clickHandler: () => handleQuickTrade(TransactionType.SELL),
            visible: showQuickTradeButton && securityValid,
            showDivider: true
        },
        {
            key: 'view-full-screen',
            element: (<>
                <ListItemIcon>
                    {!isFullScreen ? (<FullScreenIcon sx={{fontSize: '1.5rem !important'}}/>) : (<FullScreenReverseIcon sx={{fontSize: '1.5rem !important'}}/>)}
                </ListItemIcon>
                <ListItemText>
                    {!isFullScreen ? 'Maximize Chart' : 'Restore Chart'}
                </ListItemText>

            </>),
            clickHandler: showFullScreenChart,
            visible: viewKey === ChartViewKey.SYMBOL ? false : !activeLayout?.singleMode,
            showDivider: true
        },
        {
            key: 'limit-order',
            element: <>
                <ListItemIcon>
                    {
                        positionRelativeToBidAsk === 'above' ?
                            <ThinArrowDownIcon sx={{fontSize: '1.5rem !important'}}/> :
                            <ThinArrowUpIcon sx={{fontSize: '1.5rem !important'}}/>
                    }
                </ListItemIcon>
                <ListItemText>
                    {`${positionRelativeToBidAsk === 'above' ? 'Sell' : 'Buy'} 1 ${primary?.symbol} @ ${roundToNearestMultiple(Number(anchorPosition.price), positionRelativeToBidAsk === 'above' ? TransactionType.SELL : TransactionType.BUY, false, (expressionMode ? expressionSecurities[0] : security)?.tickSize / (security?.priceMultiplier ?? 100)).toFixed(2)} limit`}
                </ListItemText>
            </>,
            clickHandler: () => handleQuickChartOrder(positionRelativeToBidAsk === 'above' ? TransactionType.SELL : TransactionType.BUY, OrderType.NORMAL, Broker_OrderType.LIMIT_ORDER, Number(anchorPosition.price) * (mainSecurity?.priceMultiplier ?? 100)),
            visible: !!positionRelativeToBidAsk && !expressionMode && securityValid && Number(anchorPosition.price) > 0,
            showDivider: false
        },
        {
            key: 'stop-loss-order',
            element: <>
                <ListItemIcon>
                    {
                        positionRelativeToBidAsk === 'above' ?
                            <ThinArrowUpIcon sx={{fontSize: '1.5rem !important'}}/> :
                            <ThinArrowDownIcon sx={{fontSize: '1.5rem !important'}}/>
                    }
                </ListItemIcon>
                <ListItemText>
                    {`${positionRelativeToBidAsk === 'above' ? 'Buy' : 'Sell'} 1 ${primary?.symbol} @ ${roundToNearestMultiple(Number(anchorPosition.price), positionRelativeToBidAsk === 'above' ? TransactionType.BUY : TransactionType.SELL, false, (expressionMode ? expressionSecurities[0] : security)?.tickSize / (security?.priceMultiplier ?? 100)).toFixed(2)} stop`}
                </ListItemText>
            </>,
            clickHandler: () => handleQuickChartOrder(positionRelativeToBidAsk === 'above' ? TransactionType.BUY : TransactionType.SELL, OrderType.TRIGGER, Broker_OrderType.STOPLOSS_MARKET_ORDER, Number(anchorPosition.price) * (security?.priceMultiplier ?? 100)),
            visible: !!positionRelativeToBidAsk && !expressionMode && securityValid && Number(anchorPosition.price) > 0,
            showDivider: true
        },
        {
            key: 'buy-spread',
            element: <>
                <ListItemIcon>
                    <ThinArrowDownIcon sx={{fontSize: '1.5rem !important'}}/>
                </ListItemIcon>
                <ListItemText>
                    Buy Spread
                </ListItemText>
            </>,
            clickHandler: () => buySpread(),
            visible: expressionMode && showSpreadTradeOption,
            showDivider: false
        },
        {
            key: 'sell-spread',
            element: <>
                <ListItemIcon>
                    <ThinArrowUpIcon sx={{fontSize: '1.5rem !important'}}/>
                </ListItemIcon>
                <ListItemText>
                    Sell Spread
                </ListItemText>
            </>,
            clickHandler: () => buySpread(true),
            visible: expressionMode && showSpreadTradeOption,
            showDivider: true
        },
        {
            key: 'fut-limit-order',
            element: <>
                <ListItemIcon>
                    {
                        positionRelativeToBidAsk === 'above' ?
                            <ThinArrowDownIcon sx={{fontSize: '1.5rem !important'}}/> :
                            <ThinArrowUpIcon sx={{fontSize: '1.5rem !important'}}/>
                    }
                </ListItemIcon>
                <ListItemText>
                    {`${positionRelativeToBidAsk === 'above' ? 'Sell' : 'Buy'} 1 ${underlyingCurrentFutureSecurities[0]?.symbol} @ ${roundToNearestMultiple(Number(anchorPosition.price), positionRelativeToBidAsk === 'above' ? TransactionType.SELL : TransactionType.BUY, false, (underlyingCurrentFutureSecurities[0]?.tickSize) / (underlyingCurrentFutureSecurities[0]?.priceMultiplier ?? 100)).toFixed(2)} limit`}
                </ListItemText>
            </>,
            clickHandler: () => handleQuickTrade(positionRelativeToBidAsk === 'above' ? TransactionType.SELL : TransactionType.BUY, OrderType.NORMAL, Broker_OrderType.LIMIT_ORDER,Product.INTRADAY, roundToNearestMultiple(Number(anchorPosition.price), positionRelativeToBidAsk === 'above' ? TransactionType.SELL : TransactionType.BUY, false, (underlyingCurrentFutureSecurities[0]?.tickSize) / (underlyingCurrentFutureSecurities[0]?.priceMultiplier ?? 100)) * (security?.priceMultiplier ?? 100), true),
            visible: underlyingCurrentFutureSecurities.length > 0 && security?.hasFo && securityValid && Number(anchorPosition.price) > 0,
            showDivider: false
        },
        {
            key: 'fut-stop-loss-order',
            element: <>
                <ListItemIcon>
                    {
                        positionRelativeToBidAsk === 'above' ?
                            <ThinArrowUpIcon sx={{fontSize: '1.5rem !important'}}/> :
                            <ThinArrowDownIcon sx={{fontSize: '1.5rem !important'}}/>
                    }
                </ListItemIcon>
                <ListItemText>
                    {`${positionRelativeToBidAsk === 'above' ? 'Buy' : 'Sell'} 1 ${underlyingCurrentFutureSecurities[0]?.symbol} @ ${roundToNearestMultiple(Number(anchorPosition.price), positionRelativeToBidAsk === 'above' ? TransactionType.BUY : TransactionType.SELL, false, (underlyingCurrentFutureSecurities[0]?.tickSize) / (underlyingCurrentFutureSecurities[0]?.priceMultiplier ?? 100)).toFixed(2)} stop`}
                </ListItemText>
            </>,
            clickHandler: () => handleQuickTrade(positionRelativeToBidAsk === 'above' ? TransactionType.BUY : TransactionType.SELL, OrderType.TRIGGER, Broker_OrderType.STOPLOSS_MARKET_ORDER, Product.INTRADAY, roundToNearestMultiple(Number(anchorPosition.price), positionRelativeToBidAsk === 'above' ? TransactionType.BUY : TransactionType.SELL, false, (underlyingCurrentFutureSecurities[0]?.tickSize) / (underlyingCurrentFutureSecurities[0]?.priceMultiplier ?? 100)) * (security?.priceMultiplier ?? 100), true),
            visible: underlyingCurrentFutureSecurities.length > 0 && security?.hasFo && securityValid && Number(anchorPosition.price) > 0,
            showDivider: true
        },
        {
            key: 'trade-options',
            element: <>
                <ListItemIcon>
                    <Trade sx={{ fontSize: '1.5rem !important' }}/>
                </ListItemIcon>
                <ListItemText>Trade Options</ListItemText>
                <ListItemIcon>
                    {
                        openTradeOptions ?
                            <ThinArrowDownIcon sx={{fontSize: '1.5rem !important'}}/> :
                            <ThinArrowRightIcon sx={{fontSize: '1.5rem !important'}}/>
                    }
                </ListItemIcon>
            </>,
            clickHandler: () => {
                AnalyticsManager.logEvent(`Trade Option ${!openTradeOptions ? 'Expanded' : 'Collapsed'}`, {source: 'chart-grid'})
                if (!openTradeOptions && key) {
                    const ltp = store.getState().securityStore.snapshots[key]?.lastTradedPrice
                    if (ltp) setClickedLtp(ltp)
                }
                setOpenTradeOptions(!openTradeOptions)
            },
            visible: security?.hasFo,
            showDivider: true
        },
        {
            key: 'horizontal-line',
            element: <>
                <ListItemIcon>
                    <HorizontalLine sx={{fontSize: '1.5rem !important'}}/>
                </ListItemIcon>
                <ListItemText>
                    {`Draw horizontal line at ${anchorPosition.price}`}
                </ListItemText>
            </>,
            clickHandler: handleDrawHorizontalLine,
            visible: true,
            showDivider: true
        },
        {
            key: 'add-to-watchlist',
            element: anchorPosition.seriesGroup ?
                <AddToWatchlistMenuItem seriesGroup={anchorPosition.seriesGroup} onClosed={handleClose} contextMenuChild /> : null,
            clickHandler: () => {
                AnalyticsManager.logEvent("Add to Watchlist Clicked", {source: 'chart-grid', symbol: primary?.symbol})
            },
            visible: securityValid,
            showDivider: false
        }
    ]}, [menuOpen, resetTimeScale, showResetChartButton, anchorPosition.price, anchorPosition.seriesGroup, viewKey, contextAlertLabel, handleClose, showQuickTradeButton, isFullScreen, showFullScreenChart, activeLayout?.singleMode, positionRelativeToBidAsk, expressionMode, showSpreadTradeOption, underlyingCurrentFutureSecurities, security?.hasFo, openTradeOptions, handleDrawHorizontalLine, copyPriceToClipBoard, handleQuickTrade, handleQuickChartOrder, buySpread, securityValid, mainSecurity, isPrimaryPaneContext, paneIndicatorAlertSeriesGroups, isIndicatorPaneAlertSupported, key, indicators, primary?.symbol, expressionSecurities])

    const getRequiredListOptions = useCallback(() => {
        if (!isPrimaryPaneContext) {
            const conditionArray = anchorPosition.position === 'left' ? ['reset-chart', 'alert', 'horizontal-line'] : ['alert', 'horizontal-line']
            return optionsList.filter(option => conditionArray.includes(option.key))
        }
        const conditionArray = anchorPosition.position === 'left' ? CHART_MENU_KEYS : BUTTON_MENU_KEYS
        return optionsList.filter(option => (conditionArray.includes(option.key) || option.key.startsWith('alert-indicator-')) && (allowTradeOnChart || !tradeOnChartExclusions.includes(option.key)) )
    }, [isPrimaryPaneContext, anchorPosition.position, optionsList, allowTradeOnChart])

    
    const visibleMenuOptions = useMemo(
        () => getRequiredListOptions().filter((option) => option.visible),
        [getRequiredListOptions]
    )
    const container = useSelector(selectMultiPaneFullScreen)

    const menuAnchorOriginHorizontal = anchorPosition.anchorOriginHorizontal
        ?? (anchorPosition.position === 'left' ? 'right' : 'right')
    const menuTransformOriginHorizontal = anchorPosition.transformOriginHorizontal
        ?? (anchorPosition.position === 'left' ? 'left' : 'right')

    return (
        <>
            <AppMenu
                anchorReference="anchorPosition"
                elevation={3}
                container={container ? document.getElementById(MULTIPANE_FULLSCREEN_MODE_ID) : null}
                anchorOrigin={{
                    vertical: 'top',
                    horizontal: menuAnchorOriginHorizontal
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: menuTransformOriginHorizontal
                }}
                anchorPosition={{
                    top: anchorPosition.y,
                    left: anchorPosition.x
                }}
                open={menuOpen}
                onClose={handleClose}
                clickAwayIgnoreSelectors={[CHART_CONTEXT_MENU_SELECTOR]}
                sx={{
                    boxShadow: theme => theme.shadows[2]
                }}
                PaperProps={{
                    [CHART_CONTEXT_MENU_ATTR]: ''
                }}
                MenuListProps={{
                    sx: {
                        padding: 0
                    }
                }}
                transitionDuration={0}
            >
                {
                    visibleMenuOptions.map((option, i, array) => {
                        const result = [
                            <MenuItem key={option.key} style={{padding: '5px'}} ref={ref}
                                      onClick={option.clickHandler}>
                                {option.element}
                            </MenuItem>
                        ]
                        if (option.key === 'trade-options' && openTradeOptions) {
                            result.push(
                                <Collapse in={openTradeOptions} key={option.key + 'collapsable'} timeout="auto"
                                          unmountOnExit>
                                    <List component="div" disablePadding onClick={() => {
                                        setOpenTradeOptions(false)
                                        handleClose()
                                    }}>
                                        {
                                            getPositionOptions()
                                        }
                                    </List>
                                </Collapse>
                            )
                        }
                        if (option.showDivider && i !== array.length - 1) result.push(<Divider style={{margin: 0}}
                                                                                               key={option.key + '-divider'}/>)
                        return result
                    }).flat(1)
                }
            </AppMenu>
        </>
    )
}

export default memo(ChartContextMenuOptions)
